import React from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import {
  PlayArrow as ExecuteIcon,
  Science as ExperimentIcon,
  Edit as EditIcon,
} from '@mui/icons-material';

/**
 * ExecutionActionsSection Component
 *
 * Section for execution action buttons and descriptions.
 *
 * @param {Object} props
 * @param {boolean} props.executing - Whether execution is in progress
 * @param {string} props.templateId - Template ID for navigation
 * @param {Function} props.onExecute - Handler for execute action
 * @param {Function} props.onExecuteSimplified - Handler for execute simplified action
 * @param {Function} props.onResetForm - Handler for reset form action
 * @param {Function} props.onEditTemplate - Handler for edit template action
 */
function ExecutionActionsSection({
  executing,
  templateId,
  onExecute,
  onExecuteSimplified,
  onResetForm,
  onEditTemplate,
}) {
  return (
    <>
      {/* Execution Methods Description */}
      <Box sx={{ mt: 3, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Execution Methods
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          <strong>Execute Template:</strong> Direct execution without experiment
          routing
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>Execute with Experiments:</strong> Automatically routes
          through active experiments for A/B testing
        </Typography>
      </Box>

      {/* Execute Buttons */}
      <Box sx={{ mt: 2, display: 'flex', gap: 2, flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            type="button"
            fullWidth
            variant="contained"
            size="large"
            startIcon={
              executing ? <CircularProgress size={20} /> : <ExecuteIcon />
            }
            onClick={onExecute}
            disabled={executing}
          >
            {executing ? 'Executing...' : 'Execute Template'}
          </Button>
          <Button
            type="button"
            fullWidth
            variant="contained"
            color="secondary"
            size="large"
            startIcon={
              executing ? <CircularProgress size={20} /> : <ExperimentIcon />
            }
            onClick={onExecuteSimplified}
            disabled={executing}
          >
            {executing ? 'Executing...' : 'Execute with Experiments'}
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            type="button"
            variant="outlined"
            size="large"
            onClick={onResetForm}
            disabled={executing}
            fullWidth
          >
            Reset Form
          </Button>
          {templateId && (
            <Button
              type="button"
              variant="outlined"
              size="large"
              startIcon={<EditIcon />}
              onClick={onEditTemplate}
              disabled={executing}
              fullWidth
            >
              Edit the Template
            </Button>
          )}
        </Box>
      </Box>
    </>
  );
}

export default ExecutionActionsSection;
